<script>
  import { onMount } from 'svelte';
  import Layout from '../components/Layout.svelte';

  let videos = [];
  let loading = true;
  let slug = '';
  let file = null;
  let uploading = false;
  let progress = 0;
  let status = '';
  let error = '';

  async function loadVideos() {
    loading = true; error = '';
    try {
      const r = await fetch('/api/help/admin/videos', { credentials: 'include' });
      if (r.ok) { const d = await r.json(); videos = d.videos || []; }
      else if (r.status === 403) error = 'Admin access required.';
      else if (r.status === 401) error = 'Please sign in.';
      else error = 'Failed to load videos.';
    } catch (_e) { error = 'Failed to load videos.'; }
    loading = false;
  }
  onMount(loadVideos);

  function onFile(e) {
    file = (e.target.files && e.target.files[0]) || null;
    if (file && !slug) slug = file.name.replace(/\.[^.]+$/, '').toLowerCase().replace(/[^a-z0-9._-]/g, '-');
  }

  // Upload with progress via XHR (fetch has no upload progress).
  function putWithProgress(url, body, contentType) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', url);
      xhr.setRequestHeader('Content-Type', contentType || 'video/mp4');
      xhr.upload.onprogress = (e) => { if (e.lengthComputable) progress = Math.round((e.loaded / e.total) * 100); };
      xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error('Storage upload failed (' + xhr.status + ')')));
      xhr.onerror = () => reject(new Error('Network error during upload (check S3 CORS)'));
      xhr.send(body);
    });
  }

  async function upload() {
    error = ''; status = ''; progress = 0;
    if (!slug) { error = 'Enter a slug (this is what you reference with @video: <slug>).'; return; }
    if (!file) { error = 'Choose a video file.'; return; }
    uploading = true;
    try {
      status = 'Preparing upload…';
      const pr = await fetch('/api/help/admin/presign', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug, contentType: file.type || 'video/mp4' })
      });
      if (!pr.ok) { const e = await pr.json().catch(() => ({})); throw new Error(e.error || ('Could not get upload URL (' + pr.status + ')')); }
      const { url, slug: finalSlug, contentType } = await pr.json();
      status = 'Uploading…';
      await putWithProgress(url, file, contentType);
      status = `Uploaded. Add “@video: ${finalSlug}” to the relevant help doc.`;
      slug = ''; file = null; progress = 0;
      await loadVideos();
    } catch (e) { error = e.message; }
    uploading = false;
  }

  function fmtSize(b) {
    if (!b) return '';
    const mb = b / 1048576;
    return mb >= 1 ? mb.toFixed(1) + ' MB' : (b / 1024).toFixed(0) + ' KB';
  }
</script>

<Layout activeRoute="/admin/help-videos">
  <div class="max-w-3xl mx-auto p-6">
    <h1 class="text-2xl font-bold text-gray-900 mb-1">Help Videos</h1>
    <p class="text-gray-500 mb-6">
      Upload walkthrough videos. They're stored privately and only play for signed-in users.
      Reference one in a help doc with <code class="px-1 bg-gray-100 rounded">@video: &lt;slug&gt;</code>.
    </p>

    <div class="rounded-lg border border-gray-200 p-4 mb-8 bg-white">
      <h2 class="font-semibold text-gray-900 mb-3">Upload a video</h2>
      <label class="block text-sm text-gray-600 mb-1">Slug</label>
      <input class="border border-gray-300 rounded px-3 py-2 w-full mb-3" bind:value={slug} placeholder="connect-to-jobtread" />
      <input type="file" accept="video/*" on:change={onFile} class="mb-3 block text-sm" />
      {#if uploading && progress > 0}
        <div class="w-full bg-gray-100 rounded h-2 mb-3 overflow-hidden">
          <div class="bg-orange-500 h-2" style="width:{progress}%"></div>
        </div>
      {/if}
      <button on:click={upload} disabled={uploading}
        class="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded disabled:opacity-50">
        {uploading ? 'Uploading…' : 'Upload'}
      </button>
      {#if status}<p class="text-green-600 text-sm mt-2">{status}</p>{/if}
      {#if error}<p class="text-red-600 text-sm mt-2">{error}</p>{/if}
    </div>

    <h2 class="font-semibold text-gray-900 mb-3">Uploaded videos</h2>
    {#if loading}
      <p class="text-gray-400">Loading…</p>
    {:else if videos.length === 0}
      <p class="text-gray-400">No videos uploaded yet.</p>
    {:else}
      <table class="w-full text-sm">
        <thead>
          <tr class="text-left text-gray-500 border-b">
            <th class="py-2">Slug (use as @video)</th><th>Size</th><th>Uploaded</th>
          </tr>
        </thead>
        <tbody>
          {#each videos as v}
            <tr class="border-b border-gray-100">
              <td class="py-2 font-mono text-gray-800">{v.slug}</td>
              <td class="text-gray-600">{fmtSize(v.size)}</td>
              <td class="text-gray-600">{v.lastModified ? new Date(v.lastModified).toLocaleDateString() : ''}</td>
            </tr>
          {/each}
        </tbody>
      </table>
    {/if}
  </div>
</Layout>
